/**
 * PATH: packages/email-hub/src/feature/hooks/useInlineImageRecovery.ts
 * FILE_ID: EMAILHUB-FEATURE-HOOKS-USEINLINEIMAGERECOVERY-001
 * PURPOSE: Canonical body of this email hooks module. Lifted
 *          from apps/<tenant>/src/features/email/hooks/useInlineImageRecovery.ts
 *          (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale. One canonical
 *          body, both tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/hooks/useInlineImageRecovery.ts: thin re-export
 *     shim that points back here.
 */

import { useEffect, useRef } from 'react'

import { fetchImageViaProxy } from '../lib/imageProxy'
import {
  applyUnavailablePlaceholder,
  classifyFailedImageSrc,
  hasBeaconDeclaredSize,
  hideWhileRecovering,
  IMG_RECOVERY_ATTR,
  IMG_RETRY_CHIP_ATTR,
  retryFailedImage,
  revealRecoveredImage,
} from '../lib/inlineImageFallback'

/** How long to wait after the first failure before firing a refetch.
 *  Long enough to absorb a burst of failures from a freshly-mounted
 *  body (20 inline images failing in the same render frame is
 *  typical for HTML newsletters); short enough that the operator
 *  perceives the recovery as "the images just appeared". */
const DEBOUNCE_MS = 250

/** Maximum number of recovery refetches per emailId before the hook
 *  silently gives up. Two is enough for the legitimate case
 *  (one-shot expiry needing one refresh; backend hiccup needing a
 *  second). More than two and the failure is structural — refetching
 *  won't help and we'd just be DoS'ing our own backend. */
const MAX_RETRIES_PER_EMAIL = 2

/** How long to wait before refetching when the body contains
 *  ``data-cid-syncing`` placeholders (cid: refs whose attachment rows
 *  haven't hydrated yet). Longer than DEBOUNCE_MS because the backend
 *  attachment dispatcher genuinely needs a beat to catch up on a
 *  freshly-synced email — refetching instantly would just re-render
 *  the same placeholder and burn a retry slot. */
const CID_SYNCING_RETRY_MS = 2_000

/** Maximum number of REMOTE images we'll route through the same-origin
 *  backend proxy per email before giving up. Remote body images fail for
 *  two recoverable reasons — the sender's host blocks our cross-origin /
 *  no-referrer load (hotlink protection) — which the proxy fixes by
 *  fetching the bytes server-side. Bounded so a newsletter full of dead
 *  third-party URLs can't fan out into dozens of proxy round-trips. */
const MAX_PROXY_RECOVERIES_PER_EMAIL = 6

export interface UseInlineImageRecoveryArgs {
  /** The email currently rendered in the body. ``null`` while
   *  loading — the hook no-ops until an id arrives. */
  emailId: string | null
  /** Hand back into ``useEmail.refetch`` so the email row gets
   *  refetched (which re-signs every inline_url for another 15 min). */
  refetch: () => void
}

export interface UseInlineImageRecoveryResult {
  /** Attach to the element that contains the
   *  ``dangerouslySetInnerHTML`` body. Stable across renders. */
  bodyRef: React.RefObject<HTMLDivElement | null>
}

/**
 * Wire `<img>`-error recovery onto the email body container. See file
 * docstring for the threat model and the contract this fulfills.
 */
export function useInlineImageRecovery(
  args: UseInlineImageRecoveryArgs,
): UseInlineImageRecoveryResult {
  const { emailId, refetch } = args
  const bodyRef = useRef<HTMLDivElement | null>(null)

  // Held in a ref so the listener installed by useEffect always sees
  // the latest refetch closure without re-binding (cb identity changes
  // every render — useCallback'd in some callers, not in others).
  const refetchRef = useRef(refetch)
  refetchRef.current = refetch

  // Per-emailId retry counter. Reset to 0 whenever emailId changes
  // so a fresh email gets its own quota — bug-shape we're guarding
  // against is "stuck in retry loop for a single email", not "user
  // can never recover by clicking another email".
  const retriesRef = useRef<{ emailId: string | null; count: number }>({
    emailId: null,
    count: 0,
  })
  if (retriesRef.current.emailId !== emailId) {
    retriesRef.current = { emailId, count: 0 }
  }

  // Per-emailId budget for remote-image proxy recoveries (separate from
  // the inline refetch budget above — different backend, different cost).
  const proxyRef = useRef<{ emailId: string | null; count: number }>({
    emailId: null,
    count: 0,
  })
  if (proxyRef.current.emailId !== emailId) {
    proxyRef.current = { emailId, count: 0 }
  }

  useEffect(() => {
    const root = bodyRef.current
    if (!root || !emailId) return

    // Object URLs minted for proxy-recovered images — revoked on cleanup
    // so a long inbox session can't leak blob handles.
    const createdObjectUrls = new Set<string>()

    // Debounce timer + "did we see at least one inline failure?" flag.
    // Held in closure (not ref) because they're effect-local and
    // shouldn't survive emailId changes.
    let pendingTimer: number | null = null
    let sawInlineFailure = false

    // Queue the debounced refetch that re-signs every inline_url. A burst
    // of inline failures in one render frame collapses to a single refetch.
    const queueInlineRefetch = () => {
      sawInlineFailure = true
      if (pendingTimer !== null) return // already queued
      pendingTimer = window.setTimeout(() => {
        pendingTimer = null
        if (!sawInlineFailure) return
        sawInlineFailure = false
        const slot = retriesRef.current
        if (slot.emailId !== emailId) return // emailId changed mid-flight
        if (slot.count >= MAX_RETRIES_PER_EMAIL) return // give up silently
        slot.count += 1
        refetchRef.current()
      }, DEBOUNCE_MS)
    }

    // Try to recover a remote (third-party) image by fetching its bytes
    // through the same-origin backend proxy, which strips the CORS /
    // referrer / hotlink constraints that block a direct browser load.
    // On success swap in an object URL; on failure fall to the tile.
    const recoverRemoteViaProxy = (img: HTMLImageElement, src: string) => {
      const slot = proxyRef.current
      if (slot.emailId !== emailId || slot.count >= MAX_PROXY_RECOVERIES_PER_EMAIL) {
        applyUnavailablePlaceholder(img)
        return
      }
      slot.count += 1
      img.setAttribute(IMG_RECOVERY_ATTR, 'proxying')
      void fetchImageViaProxy(src).then((blob) => {
        // emailId changed while the proxy was in flight — drop the result.
        if (proxyRef.current.emailId !== emailId) return
        if (blob) {
          const objectUrl = URL.createObjectURL(blob)
          createdObjectUrls.add(objectUrl)
          img.setAttribute(IMG_RECOVERY_ATTR, 'proxied')
          // Bring the element back from the on-error collapse now that we
          // have real bytes to paint (see `hideWhileRecovering` below).
          revealRecoveredImage(img)
          img.src = objectUrl
        } else {
          applyUnavailablePlaceholder(img)
        }
      })
    }

    const onError = (e: Event) => {
      const target = e.target
      if (!(target instanceof HTMLImageElement)) return
      // Already in a recovery flow (or terminally failed) — never
      // reprocess the same element or we'd loop.
      const state = target.getAttribute(IMG_RECOVERY_ATTR)
      if (state === 'failed' || state === 'proxying') return

      const src = target.currentSrc || target.src || ''

      // Collapse the broken box IMMEDIATELY — before the (async) recovery
      // below runs — so the operator never sees the native broken-image
      // glyph flash on screen while an inline refetch (250 ms debounce +
      // GET) or a remote proxy round-trip (0.5–3 s) is in flight. Real
      // bytes re-reveal the element (proxy success / inline re-render);
      // a dead image just stays collapsed, which is also its terminal
      // state. This is the "still glitches on load" fix (REC-0002).
      hideWhileRecovering(target)

      // Declared 1x1/2x2 spacers never go through the proxy — even when the
      // URL looks like a normal CDN path. naturalWidth is 0 after error, so
      // we only trust declared attributes here.
      if (hasBeaconDeclaredSize(target)) {
        applyUnavailablePlaceholder(target)
        return
      }

      switch (classifyFailedImageSrc(src)) {
        case 'inline': {
          // Our signed inline URL expired / 410'd. Refetch re-signs it —
          // but only while the budget lasts. Once spent, the bytes truly
          // aren't coming, so show the calm tile instead of a broken box.
          const slot = retriesRef.current
          if (slot.emailId === emailId && slot.count >= MAX_RETRIES_PER_EMAIL) {
            applyUnavailablePlaceholder(target)
            return
          }
          queueInlineRefetch()
          return
        }
        case 'remote':
          // Hotlink/CORS/referrer-blocked third-party image — recover via
          // the backend proxy (bounded), else tile it.
          recoverRemoteViaProxy(target, src)
          return
        case 'beacon':
        default:
          // Beacons, data:/blob:/relative — not network recoverable (and
          // beacons must not be fetched server-side). Silent collapse.
          applyUnavailablePlaceholder(target)
          return
      }
    }

    // `error` doesn't bubble — but it DOES fire in the capture phase
    // on every ancestor, so `capture: true` on the body container
    // catches every `<img>` failure inside.
    root.addEventListener('error', onError, true)

    // "Image unavailable — tap to retry" chip (MR-C): a tap resets both
    // per-email budgets (a MANUAL retry is the user saying "wifi is back,
    // try again" — it deserves a fresh window, not the leftovers of the
    // automatic attempts) and re-runs the pipeline from the top via
    // retryFailedImage (restore src → load or error → classify → recover).
    const onChipClick = (e: Event) => {
      const target = e.target
      if (!(target instanceof Element)) return
      const chip = target.closest(`[${IMG_RETRY_CHIP_ATTR}]`)
      if (!chip || !root.contains(chip)) return
      e.preventDefault()
      e.stopPropagation()
      retriesRef.current = { emailId, count: 0 }
      proxyRef.current = { emailId, count: 0 }
      retryFailedImage(chip)
    }
    root.addEventListener('click', onChipClick, true)

    // "Image syncing… reload in a moment" placeholders previously had
    // NO backing — the sanitizer substituted them for cid: refs with
    // no attachment row, but nothing ever refetched, so they could sit
    // there forever (the data-URI src loads fine, so the error-event
    // path above never fires for them). Give the promise a backing:
    // if the rendered body contains any placeholder, schedule a
    // bounded refetch so the re-signed/now-hydrated attachment rows
    // can resolve the cid on the next sanitize pass. Shares the same
    // per-email retry quota as the expiry path so a structurally
    // missing attachment can't loop us.
    let cidTimer: number | null = null
    const scheduleCidRetry = () => {
      if (cidTimer !== null) return // already queued
      const slot = retriesRef.current
      if (slot.emailId !== emailId || slot.count >= MAX_RETRIES_PER_EMAIL) return
      cidTimer = window.setTimeout(() => {
        cidTimer = null
        const s = retriesRef.current
        if (s.emailId !== emailId) return
        if (s.count >= MAX_RETRIES_PER_EMAIL) return
        // Re-check the live DOM — the placeholder may have resolved
        // via a poll/refetch that happened while we were waiting.
        if (!root.querySelector('img[data-cid-syncing]')) return
        s.count += 1
        refetchRef.current()
      }, CID_SYNCING_RETRY_MS)
    }
    // Placeholders already in the DOM at mount (body was in the list
    // payload) …
    if (root.querySelector('img[data-cid-syncing]')) scheduleCidRetry()
    // … and placeholders injected later (detail GET lands after this
    // effect ran — same emailId, so the effect does NOT re-run). The
    // placeholder's data-URI src fires `load` in the capture phase,
    // which is our signal that one just appeared.
    const onLoad = (e: Event) => {
      const t = e.target
      if (t instanceof HTMLImageElement && t.hasAttribute('data-cid-syncing')) {
        scheduleCidRetry()
      }
    }
    root.addEventListener('load', onLoad, true)

    return () => {
      root.removeEventListener('click', onChipClick, true)
      root.removeEventListener('load', onLoad, true)
      root.removeEventListener('error', onError, true)
      if (pendingTimer !== null) {
        window.clearTimeout(pendingTimer)
        pendingTimer = null
      }
      if (cidTimer !== null) {
        window.clearTimeout(cidTimer)
        cidTimer = null
      }
      // Release any blob handles minted for proxy-recovered images.
      for (const url of createdObjectUrls) URL.revokeObjectURL(url)
      createdObjectUrls.clear()
    }
  }, [emailId])

  return { bodyRef }
}
